import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Eye, EyeOff, Music, ArrowLeft, RefreshCw } from 'lucide-react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [siteSettings, setSiteSettings] = useState({});
  const [twoFA, setTwoFA] = useState(null); // { temp_token }
  const [code, setCode] = useState('');
  const [resending, setResending] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get('/site-info'); setSiteSettings(data); } catch (e) {}
    })();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post('/auth/login', { email, password });
      if (data.requires_2fa) {
        setTwoFA({ temp_token: data.temp_token });
        toast.success('Codice di verifica inviato alla tua email');
      } else {
        localStorage.setItem('accessToken', data.token);
        toast.success('Accesso effettuato!');
        window.location.href = '/dashboard';
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'Errore durante il login';
      toast.error(msg);
    } finally { setLoading(false); }
  };

  const verify2FA = async () => {
    if (!code || code.length !== 6) return toast.error('Inserisci il codice a 6 cifre');
    setLoading(true);
    try {
      const { data } = await api.post('/auth/verify-2fa', { temp_token: twoFA.temp_token, code });
      localStorage.setItem('accessToken', data.token);
      toast.success('Accesso effettuato!');
      window.location.href = '/dashboard';
    } catch (err) {
      toast.error(err.response?.data?.error || 'Codice errato');
    } finally { setLoading(false); }
  };

  const resendCode = async () => {
    setResending(true);
    try {
      await api.post('/auth/resend-2fa', { temp_token: twoFA.temp_token });
      toast.success('Nuovo codice inviato!');
    } catch (err) { toast.error(err.response?.data?.error || 'Errore'); }
    finally { setResending(false); }
  };

  const siteName = siteSettings.site_name || 'PROUD NOIZE';
  const logoPath = siteSettings.site_logo_path;

  return (
    <div className="min-h-screen bg-[#1A1A2E] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          {logoPath ? (
            <img src={logoPath} alt={siteName} className="h-20 mx-auto mb-4 object-contain" />
          ) : (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#E94560] mb-4">
                <Music size={32} className="text-white" />
              </div>
              <h1 className="text-3xl font-bold text-white tracking-wider">{siteName}</h1>
            </>
          )}
          <p className="text-gray-400 mt-2">Workstation di Distribuzione Musicale</p>
        </div>

        {!twoFA ? (
          /* Login Form */
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-8">
            <h2 className="text-xl font-bold text-[#1A1A2E] mb-6">Accedi al tuo account</h2>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#E94560] focus:border-transparent outline-none transition"
                placeholder="la-tua@email.com" />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <div className="relative">
                <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#E94560] focus:border-transparent outline-none transition pr-12"
                  placeholder="La tua password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="flex justify-end mb-6">
              <Link to="/forgot-password" className="text-sm text-[#E94560] hover:underline">Password dimenticata?</Link>
            </div>

            <button type="submit" disabled={loading}
              className="w-full py-3 px-4 rounded-lg bg-[#E94560] text-white font-medium hover:bg-[#d63b55] transition disabled:opacity-50 disabled:cursor-not-allowed">
              {loading ? 'Accesso in corso...' : 'Accedi'}
            </button>

            {siteSettings.registration_enabled !== 'false' && (
              <p className="text-center text-sm text-gray-500 mt-6">
                Non hai un account?{' '}
                <Link to="/register" className="text-[#E94560] font-medium hover:underline">Registrati</Link>
              </p>
            )}
          </form>
        ) : (
          /* 2FA Code Input */
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <button onClick={() => { setTwoFA(null); setCode(''); }} className="text-gray-400 hover:text-gray-600 mb-4 flex items-center gap-1 text-sm">
              <ArrowLeft size={16} /> Torna al login
            </button>
            <h2 className="text-xl font-bold text-[#1A1A2E] mb-2">Verifica in due passaggi</h2>
            <p className="text-sm text-gray-500 mb-6">Abbiamo inviato un codice a 6 cifre a <strong>{email}</strong></p>

            <div className="mb-6">
              <input type="text" value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full px-4 py-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#E94560] focus:border-transparent outline-none transition text-center text-2xl font-bold tracking-[0.5em]"
                placeholder="000000" maxLength={6} autoFocus
                onKeyDown={e => { if (e.key === 'Enter' && code.length === 6) verify2FA(); }} />
            </div>

            <button onClick={verify2FA} disabled={loading || code.length !== 6}
              className="w-full py-3 px-4 rounded-lg bg-[#E94560] text-white font-medium hover:bg-[#d63b55] transition disabled:opacity-50 disabled:cursor-not-allowed">
              {loading ? 'Verifica...' : 'Verifica Codice'}
            </button>

            <div className="text-center mt-4">
              <button onClick={resendCode} disabled={resending} className="text-sm text-[#E94560] hover:underline inline-flex items-center gap-1">
                <RefreshCw size={14} className={resending ? 'animate-spin' : ''} />
                {resending ? 'Invio...' : 'Non hai ricevuto il codice? Reinvia'}
              </button>
            </div>
          </div>
        )}

        <div className="flex justify-center gap-4 mt-6 text-xs text-gray-500">
          <Link to="/legal/privacy" className="hover:text-gray-300">Privacy</Link>
          <Link to="/legal/cookies" className="hover:text-gray-300">Cookie</Link>
          <Link to="/legal/antifraud" className="hover:text-gray-300">Anti-Fraud</Link>
        </div>
      </div>
    </div>
  );
}
